import React, { useState } from "react";
import { observer } from "mobx-react";
import theme from "Theme/AppTheme";
import AcxMainTextField from "components/UI/AcxMainTextField";
import Statement from "./Statement";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { FieldOptionsType, StatementType } from "../../types";
import useDebouncedUpdate from "../../hooks/useDebouncedUpdate";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import { selectCustomStyle } from "../../helpers/componentUtils";

type ResultStatementProps = {
    statement?: StatementType;
    fieldOptions: FieldOptionsType[];
    updateResult: (value: {
        result?: string;
        isLiteral: boolean;
        sourceName?: string;
    }) => void;
    removeResult: () => void;
};

const ResultStatement: React.FC<ResultStatementProps> = observer(
    ({ statement, updateResult, removeResult, fieldOptions }) => {
        const { result, id, hasError, isLiteral, sourceName } = statement || {};
        const [value, setValue] = useState({
            result: result,
            isLiteral: isLiteral || false,
            sourceName: sourceName,
        });

        const handleChange = (key: string, val: string | boolean) => {
            let updatedResult = value.result;
            let updatedSourceName = value.sourceName;

            // only one of result or sourceName can be set at a time
            if (key === "result") {
                updatedSourceName = undefined;
                updatedResult = val as string;
            } else if (key === "sourceName") {
                updatedResult = undefined;
                updatedSourceName = val as string;
            }
            setValue((prev) => ({
                ...prev,
                [key]: val,
                result: updatedResult,
                sourceName: updatedSourceName,
            }));
        };

        useDebouncedUpdate(() => {
            updateResult(value);
        }, [value]);

        return (
            <Statement
                remove={removeResult}
                label="RESULT"
                sx={{ gap: theme.spacing(2), marginLeft: theme.spacing(2) }}
            >
                <Box
                    sx={{
                        background: theme.palette.gray[100],
                        width: "100%",
                        display: "flex",
                        gap: theme.spacing(2),
                        padding: theme.spacing(2),
                        borderRadius: 2,
                    }}
                >
                    <Typography
                        sx={{ alignSelf: "center", fontWeight: 600 }}
                        variant="body1"
                    >
                        Persist
                    </Typography>

                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={statement?.isLiteral || false}
                                onChange={(e) =>
                                    handleChange("isLiteral", e.target.checked)
                                }
                                sx={{
                                    padding: 1,
                                }}
                            />
                        }
                        label="Is literal"
                        sx={{
                            paddingRight: 2,
                            borderRadius: 1,
                            margin: 0,
                        }}
                    />

                    {value.isLiteral ? (
                        <AcxMainTextField
                            id={`result-value-${id}`}
                            placeholderText="Enter value"
                            value={value.result}
                            fullWidth
                            containerStyle={{ flex: 1 }}
                            onChange={(e) =>
                                handleChange("result", e.target.value)
                            }
                            error={hasError && !value}
                            hideErrorIcon
                        />
                    ) : (
                        <AcxSelect
                            fullWidth
                            customStyle={selectCustomStyle({ flex: 1 })}
                            id={`result-sourceName-${id}`}
                            options={fieldOptions}
                            placeholder="Select field"
                            defaultValue={fieldOptions.find(
                                (option) => option.value === value.sourceName,
                            )}
                            error={hasError && !value}
                            onChange={(e) =>
                                handleChange("sourceName", e.value)
                            }
                        />
                    )}
                </Box>
            </Statement>
        );
    },
);

export default ResultStatement;
